import { apiClient } from './api'

interface GoogleAuthResponse {
  credential?: string
  clientId?: string
  select_by?: string
}

interface OAuthCallbackData {
  code?: string
  id_token?: string
  access_token?: string
  email?: string
  name?: string
  picture?: string
}

class OAuthService {
  private googleInitialized = false
  private googleClientId: string

  constructor() {
    this.googleClientId = import.meta.env.VITE_GOOGLE_CLIENT_ID || ''
  }

  // Initialize Google Sign-In
  async initializeGoogle(): Promise<void> {
    if (this.googleInitialized) return

    if (!this.googleClientId) {
      throw new Error('VITE_GOOGLE_CLIENT_ID is not set')
    }

    // Load the Google Sign-In script
    await this.loadGoogleScript()

    // Initialize Google Sign-In with error handling
    window.google.accounts.id.initialize({
      client_id: this.googleClientId,
      callback: this.handleGoogleCredentialResponse.bind(this),
      auto_select: false,
      cancel_on_tap_outside: true,
      error_callback: () => {
      }
    } as any)

    this.googleInitialized = true
  }

  // Load Google Sign-In script
  private loadGoogleScript(): Promise<void> {
    return new Promise((resolve, reject) => {
      if (window.google?.accounts?.id) {
        resolve()
        return
      }

      const script = document.createElement('script')
      script.src = 'https://accounts.google.com/gsi/client'
      script.async = true
      script.defer = true
      script.onload = () => resolve()
      script.onerror = () => reject(new Error('Failed to load Google Sign-In script'))
      document.head.appendChild(script)
    })
  }

  // Handle Google credential response
  private async handleGoogleCredentialResponse(response: GoogleAuthResponse) {
    if (!response.credential) {
      // No credential received
      return
    }

    try {
      // Send the ID token to the backend
      const result = await apiClient.request('post', '/auth/oauth/google', {
        auth_data: {
          id_token: response.credential,
          client_id: response.clientId
        }
      })

      // The API wraps data in { success, data: { user, token, ... } }
      const payload = result.data?.data || result.data
      const { user, token, is_new_user } = payload

      if (token) {
        // Store in localStorage (same as normal login)
        localStorage.setItem('auth_token', token)
        localStorage.setItem('cached_user', JSON.stringify(user))

        // Redirect — full page reload ensures the auth store picks up the token
        if (is_new_user) {
          window.location.href = '/plans'
        } else {
          window.location.href = '/dashboard'
        }
      }
    } catch (error) {
      // Authentication failed silently
    }
  }

  // Show Google One Tap prompt
  async showGoogleOneTap(): Promise<void> {
    if (!this.googleInitialized) {
      await this.initializeGoogle()
    }

    window.google.accounts.id.prompt(() => {
    })
  }

  // Render Google Sign-In button
  async renderGoogleButton(containerId: string, options?: any): Promise<void> {
    if (!this.googleInitialized) {
      await this.initializeGoogle()
    }

    const container = document.getElementById(containerId)
    if (!container) {
      throw new Error(`Container with id "${containerId}" not found`)
    }

    // Use the container's width, falling back to a sensible default
    const width = container.offsetWidth > 0 ? container.offsetWidth : 300

    window.google.accounts.id.renderButton(container, {
      type: 'standard',
      shape: 'rectangular',
      theme: 'outline',
      text: 'continue_with',
      size: 'large',
      logo_alignment: 'left',
      width,
      ...options
    })
  }

  // Handle OAuth callback (for redirect flow)
  async handleOAuthCallback(provider: string, params: URLSearchParams): Promise<void> {
    const code = params.get('code')
    const state = params.get('state')
    const error = params.get('error')

    if (error) {
      throw new Error(`OAuth error: ${error}`)
    }

    if (!code) {
      throw new Error('No authorization code received')
    }

    try {
      // Exchange code for tokens on the backend
      const result = await apiClient.request('post', `/auth/oauth/${provider}`, {
        auth_data: {
          code,
          state,
          redirect_uri: `${window.location.origin}/auth/callback/${provider}`
        }
      })

      // Store auth data and redirect
      if (result.data.token) {
        localStorage.setItem('auth_token', result.data.token)
        localStorage.setItem('user', JSON.stringify(result.data.user))
        
        // Redirect to plans page for new users, dashboard for existing
        if (result.data.is_new_user) {
          window.location.href = '/users/plans'
        } else {
          window.location.href = '/dashboard'
        }
      }
    } catch (error) {
      throw error
    }
  }

  // Revoke Google access
  async revokeGoogleAccess(hint?: string): Promise<void> {
    if (!window.google?.accounts?.id) return

    window.google.accounts.id.disableAutoSelect()
    
    if (hint) {
      window.google.accounts.id.revoke(hint, () => {
      })
    }
  }
}

// Create singleton instance
export const oauthService = new OAuthService()

